import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  authMe,
  authLogout,
  adminListPosts,
  adminCreatePost,
  adminUpdatePost,
  adminDeletePost,
  adminListSubmissions,
  adminListBusinessApplications,
} from "@/lib/api";
import { Loader2, Plus, Pencil, Trash2, LogOut, FileText, Inbox, X, Eye, Briefcase } from "lucide-react";

export default function AdminDashboard() {
  const location = useLocation();
  const navigate = useNavigate();
  const [user, setUser] = useState(location.state?.user || null);
  const [checking, setChecking] = useState(!location.state?.user);
  const [tab, setTab] = useState("posts");

  useEffect(() => {
    if (window.location.hash?.includes("session_id=")) return;
    if (user) return;
    authMe()
      .then((u) => {
        if (!u.is_admin) {
          navigate("/admin/login?error=not_admin", { replace: true });
        } else {
          setUser(u);
        }
      })
      .catch(() => navigate("/admin/login", { replace: true }))
      .finally(() => setChecking(false));
  }, [user, navigate]);

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A] text-white">
        <Loader2 className="h-6 w-6 animate-spin text-[#FE7200]" />
      </div>
    );
  }
  if (!user) return null;

  const handleLogout = async () => {
    await authLogout().catch(() => {});
    navigate("/admin/login", { replace: true });
  };

  return (
    <div data-testid="admin-dashboard-page" className="min-h-screen bg-[#0A0A0A] text-white">
      <header className="border-b border-white/10 bg-black/60 backdrop-blur-xl sticky top-0 z-10">
        <div className="mx-auto max-w-7xl px-4 md:px-8 lg:px-12 h-16 flex items-center justify-between">
          <Link to="/" data-testid="admin-site-link" className="font-display font-extrabold tracking-tight">
            Anytime <span className="text-[#FE7200]">Admin</span>
          </Link>
          <div className="flex items-center gap-3">
            <span className="hidden sm:inline text-sm text-zinc-400">{user.email}</span>
            {user.picture && <img src={user.picture} alt="" className="h-8 w-8 rounded-full border border-white/10" />}
            <button
              onClick={handleLogout}
              data-testid="admin-logout-button"
              className="inline-flex items-center gap-1.5 rounded-md border border-white/15 bg-white/5 px-3 py-1.5 text-xs font-semibold hover:bg-white/10 transition-colors"
            >
              <LogOut className="h-3.5 w-3.5" /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-7xl px-4 md:px-8 lg:px-12 py-10">
        <div className="flex items-center gap-2 border-b border-white/10 overflow-x-auto">
          <TabButton active={tab === "posts"} onClick={() => setTab("posts")} icon={FileText} label="Blog posts" testId="tab-posts" />
          <TabButton active={tab === "subs"} onClick={() => setTab("subs")} icon={Inbox} label="Form submissions" testId="tab-submissions" />
          <TabButton active={tab === "biz"} onClick={() => setTab("biz")} icon={Briefcase} label="Business applications" testId="tab-business-applications" />
        </div>

        <div className="mt-8">
          {tab === "posts" && <PostsManager />}
          {tab === "subs" && <SubmissionsList />}
          {tab === "biz" && <BusinessApplicationsList />}
        </div>
      </div>
    </div>
  );
}

function TabButton({ active, onClick, icon: Icon, label, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`inline-flex items-center gap-2 px-4 py-3 text-sm font-semibold border-b-2 transition-colors ${
        active ? "text-[#FE7200] border-[#FE7200]" : "text-zinc-400 border-transparent hover:text-white"
      }`}
    >
      <Icon className="h-4 w-4" /> {label}
    </button>
  );
}

function PostsManager() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // post object or "new"

  const refresh = () => {
    setLoading(true);
    adminListPosts()
      .then(setPosts)
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    refresh();
  }, []);

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this post?")) return;
    await adminDeletePost(id);
    refresh();
  };

  return (
    <div data-testid="posts-manager">
      <div className="flex items-center justify-between">
        <h2 className="font-display text-2xl font-bold">Blog posts</h2>
        <button
          onClick={() => setEditing("new")}
          data-testid="new-post-button"
          className="inline-flex items-center gap-1.5 rounded-full bg-[#FE7200] px-4 py-2 text-xs font-bold uppercase tracking-wider text-black hover:bg-[#FF8C2E] transition-colors"
        >
          <Plus className="h-4 w-4" /> New Post
        </button>
      </div>

      {loading ? (
        <div className="mt-8 text-zinc-400">Loading…</div>
      ) : (
        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          {posts.length === 0 && (
            <div className="rounded-xl border border-white/10 bg-[#121212] p-6 text-zinc-400">No posts yet. Click "New Post".</div>
          )}
          {posts.map((p, i) => (
            <div
              key={p.id}
              data-testid={`admin-post-card-${i}`}
              className="rounded-xl border border-white/10 bg-[#121212] p-5"
            >
              <div className="flex items-center gap-2 text-xs">
                <span className="inline-flex items-center rounded-full bg-[#FE7200] px-2.5 py-0.5 font-bold uppercase tracking-wider text-black">
                  {p.category}
                </span>
                {!p.published && (
                  <span className="inline-flex items-center rounded-full bg-zinc-700 px-2.5 py-0.5 font-bold uppercase tracking-wider text-white">Draft</span>
                )}
                <span className="text-zinc-500">·</span>
                <span className="text-zinc-500">{new Date(p.updated_at).toLocaleDateString()}</span>
              </div>
              <h3 className="mt-3 font-display text-lg font-bold leading-snug">{p.title}</h3>
              <p className="mt-1 text-sm text-zinc-400 line-clamp-2">{p.excerpt}</p>
              <div className="mt-4 flex items-center gap-2">
                <button
                  onClick={() => setEditing(p)}
                  data-testid={`edit-post-${i}`}
                  className="inline-flex items-center gap-1.5 rounded-md border border-white/15 bg-white/5 px-3 py-1.5 text-xs font-semibold hover:bg-white/10 transition-colors"
                >
                  <Pencil className="h-3.5 w-3.5" /> Edit
                </button>
                <button
                  onClick={() => handleDelete(p.id)}
                  data-testid={`delete-post-${i}`}
                  className="inline-flex items-center gap-1.5 rounded-md border border-red-500/30 bg-red-500/10 px-3 py-1.5 text-xs font-semibold text-red-300 hover:bg-red-500/20 transition-colors"
                >
                  <Trash2 className="h-3.5 w-3.5" /> Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <PostEditor
          post={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            refresh();
          }}
        />
      )}
    </div>
  );
}

function PostEditor({ post, onClose, onSaved }) {
  const [form, setForm] = useState({
    title: post?.title || "",
    category: post?.category || "Updates",
    excerpt: post?.excerpt || "",
    content: post?.content || "",
    cover_url: post?.cover_url || "",
    published: post?.published ?? true,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const handleSave = async () => {
    setSaving(true);
    setError("");
    try {
      if (post) await adminUpdatePost(post.id, form);
      else await adminCreatePost(form);
      onSaved();
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to save");
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center px-4 py-8"
      data-testid="post-editor-modal"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="w-full max-w-3xl max-h-full overflow-y-auto rounded-2xl border border-white/10 bg-[#0f0f10]">
        <div className="flex items-center justify-between p-5 border-b border-white/10">
          <h3 className="font-display text-lg font-bold">{post ? "Edit post" : "New post"}</h3>
          <button onClick={onClose} data-testid="close-editor" className="text-zinc-400 hover:text-white">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <Input label="Title" value={form.title} onChange={update("title")} testId="post-title-input" />
          <div className="grid grid-cols-2 gap-4">
            <Input label="Category" value={form.category} onChange={update("category")} testId="post-category-input" />
            <Input label="Cover image URL (optional)" value={form.cover_url} onChange={update("cover_url")} testId="post-cover-input" />
          </div>
          <Textarea label="Excerpt" rows={2} value={form.excerpt} onChange={update("excerpt")} testId="post-excerpt-input" />
          <Textarea label="Content" rows={10} value={form.content} onChange={update("content")} testId="post-content-input" />
          <label className="inline-flex items-center gap-2 text-sm text-zinc-300">
            <input type="checkbox" data-testid="post-published-checkbox" checked={form.published} onChange={update("published")} />
            Published
          </label>
          {error && <div className="rounded-md border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs text-red-200">{error}</div>}
        </div>
        <div className="flex items-center justify-end gap-2 p-5 border-t border-white/10">
          <button onClick={onClose} className="rounded-md border border-white/15 bg-white/5 px-4 py-2 text-sm hover:bg-white/10">Cancel</button>
          <button
            onClick={handleSave}
            disabled={saving || !form.title}
            data-testid="save-post-button"
            className="inline-flex items-center gap-2 rounded-md bg-[#FE7200] px-4 py-2 text-sm font-bold text-black hover:bg-[#FF8C2E] disabled:opacity-60"
          >
            {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            Save
          </button>
        </div>
      </div>
    </div>
  );
}

function Input({ label, value, onChange, testId }) {
  return (
    <label className="block">
      <span className="block text-xs font-semibold text-zinc-300 mb-1.5">{label}</span>
      <input
        value={value}
        onChange={onChange}
        data-testid={testId}
        className="w-full rounded-md bg-[#1A1A1A] border border-white/10 px-3 py-2 text-sm text-white focus:outline-none focus:border-[#FE7200] focus:ring-2 focus:ring-[#FE7200]/30"
      />
    </label>
  );
}

function Textarea({ label, rows = 4, value, onChange, testId }) {
  return (
    <label className="block">
      <span className="block text-xs font-semibold text-zinc-300 mb-1.5">{label}</span>
      <textarea
        rows={rows}
        value={value}
        onChange={onChange}
        data-testid={testId}
        className="w-full rounded-md bg-[#1A1A1A] border border-white/10 px-3 py-2 text-sm text-white focus:outline-none focus:border-[#FE7200] focus:ring-2 focus:ring-[#FE7200]/30"
      />
    </label>
  );
}

function SubmissionsList() {
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    adminListSubmissions()
      .then(setSubs)
      .finally(() => setLoading(false));
  }, []);

  return (
    <div data-testid="submissions-list">
      <h2 className="font-display text-2xl font-bold">Form submissions</h2>
      <p className="mt-2 text-sm text-zinc-400">
        All leads from the site visit / contact forms. Click any row to view full details.
      </p>

      {loading ? (
        <div className="mt-8 text-zinc-400">Loading…</div>
      ) : subs.length === 0 ? (
        <div className="mt-8 rounded-xl border border-white/10 bg-[#121212] p-6 text-zinc-400">No submissions yet.</div>
      ) : (
        <div className="mt-6 overflow-x-auto rounded-xl border border-white/10">
          <table className="w-full text-sm">
            <thead className="bg-[#121212] text-left">
              <tr>
                {["Date", "Name", "Company", "Email", "Phone", "Preferred Visit", "Source", ""].map((h) => (
                  <th key={h} className="px-4 py-3 text-xs font-bold uppercase tracking-wider text-zinc-400">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {subs.map((s, i) => (
                <tr
                  key={s.id}
                  data-testid={`submission-row-${i}`}
                  onClick={() => setSelected(s)}
                  className="hover:bg-white/[0.04] cursor-pointer transition-colors"
                >
                  <td className="px-4 py-3 text-zinc-300 whitespace-nowrap">{new Date(s.created_at).toLocaleString()}</td>
                  <td className="px-4 py-3 text-white">{s.full_name}</td>
                  <td className="px-4 py-3 text-zinc-300">{s.company_name || "—"}</td>
                  <td className="px-4 py-3 text-zinc-300">
                    <a href={`mailto:${s.email}`} onClick={(e) => e.stopPropagation()} className="hover:text-[#FE7200]">{s.email}</a>
                  </td>
                  <td className="px-4 py-3 text-zinc-300">{s.phone || "—"}</td>
                  <td className="px-4 py-3 text-zinc-300 whitespace-nowrap">
                    {s.preferred_date || s.preferred_time ? `${s.preferred_date || ""}${s.preferred_time ? ` · ${s.preferred_time}` : ""}` : "—"}
                  </td>
                  <td className="px-4 py-3"><span className="rounded-full bg-white/5 px-2 py-0.5 text-xs">{s.source}</span></td>
                  <td className="px-4 py-3 text-right">
                    <span
                      data-testid={`view-submission-${i}`}
                      className="inline-flex items-center gap-1 rounded-md border border-white/15 bg-white/5 px-2.5 py-1 text-xs font-semibold text-zinc-200 hover:bg-white/10"
                    >
                      <Eye className="h-3.5 w-3.5" /> View
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selected && (
        <SubmissionDetail
          submission={selected}
          onClose={() => setSelected(null)}
        />
      )}
    </div>
  );
}

function SubmissionDetail({ submission: s, onClose }) {
  const rows = [
    ["Submitted", new Date(s.created_at).toLocaleString()],
    ["Source", s.source],
    ["Full name", s.full_name],
    ["Email", s.email],
    ["Phone", s.phone],
    ["Company name", s.company_name],
    ["Website / Social", s.website],
    ["Position", s.position],
    ["Employees / Foot traffic", s.employees],
    ["People at site", s.people_at_site],
    ["Company address", s.company_address],
    ["Preferred date", s.preferred_date],
    ["Preferred time", s.preferred_time],
    ["Machine location", s.machine_location],
  ];

  return (
    <div
      data-testid="submission-detail-modal"
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center px-4 py-8"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="w-full max-w-2xl max-h-full overflow-y-auto rounded-2xl border border-white/10 bg-[#0f0f10]">
        <div className="flex items-center justify-between p-5 border-b border-white/10 sticky top-0 bg-[#0f0f10]">
          <div>
            <h3 className="font-display text-lg font-bold">{s.full_name}</h3>
            <div className="mt-0.5 text-xs text-zinc-400">{s.company_name || "—"} · {s.source}</div>
          </div>
          <button
            onClick={onClose}
            data-testid="close-submission-detail"
            className="rounded-md border border-white/15 bg-white/5 p-1.5 text-zinc-400 hover:text-white hover:bg-white/10"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="p-5">
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
            {rows.map(([label, value]) => (
              <div key={label}>
                <dt className="text-[11px] font-bold uppercase tracking-wider text-zinc-500">{label}</dt>
                <dd className="mt-0.5 text-sm text-white break-words">
                  {value && String(value).trim() ? (
                    label === "Email" ? (
                      <a href={`mailto:${value}`} className="text-[#FE7200] hover:underline">{value}</a>
                    ) : label === "Phone" ? (
                      <a href={`tel:${value}`} className="text-[#FE7200] hover:underline">{value}</a>
                    ) : label === "Website / Social" ? (
                      <a href={value} target="_blank" rel="noopener noreferrer" className="text-[#FE7200] hover:underline">{value}</a>
                    ) : (
                      String(value)
                    )
                  ) : (
                    <span className="text-zinc-500">—</span>
                  )}
                </dd>
              </div>
            ))}
          </dl>

          <div className="mt-6 pt-5 border-t border-white/10">
            <div className="text-[11px] font-bold uppercase tracking-wider text-zinc-500">Notes / Message</div>
            <div className="mt-1.5 whitespace-pre-wrap rounded-lg border border-white/10 bg-[#1A1A1A] p-3.5 text-sm text-zinc-200 min-h-[80px]">
              {s.notes && s.notes.trim() ? s.notes : <span className="text-zinc-500">No notes provided.</span>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function BusinessApplicationsList() {
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    adminListBusinessApplications()
      .then(setApps)
      .catch((e) =>
        setError(e?.response?.data?.detail || "Failed to load applications."),
      )
      .finally(() => setLoading(false));
  }, []);

  return (
    <div data-testid="business-applications-list">
      <h2 className="font-display text-2xl font-bold">Business applications</h2>
      <p className="mt-2 text-sm text-zinc-400">
        Applications from the "Start Your Own Vending Business" landing page. Click any row for full details.
      </p>

      {loading ? (
        <div className="mt-8 text-zinc-400">Loading…</div>
      ) : error ? (
        <div
          data-testid="business-applications-error"
          className="mt-8 rounded-xl border border-red-500/40 bg-red-500/10 p-6 text-sm text-red-200"
        >
          {error}
        </div>
      ) : apps.length === 0 ? (
        <div className="mt-8 rounded-xl border border-white/10 bg-[#121212] p-6 text-zinc-400">No applications yet.</div>
      ) : (
        <div className="mt-6 overflow-x-auto rounded-xl border border-white/10">
          <table className="w-full text-sm">
            <thead className="bg-[#121212] text-left">
              <tr>
                {["Date", "Name", "Email", "Phone", "Country", "Investment", "Machines", "Start", ""].map((h) => (
                  <th key={h} className="px-4 py-3 text-xs font-bold uppercase tracking-wider text-zinc-400">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {apps.map((a, i) => (
                <tr
                  key={a.id}
                  data-testid={`business-app-row-${i}`}
                  onClick={() => setSelected(a)}
                  className="hover:bg-white/[0.04] cursor-pointer transition-colors"
                >
                  <td className="px-4 py-3 text-zinc-300 whitespace-nowrap">{new Date(a.created_at).toLocaleString()}</td>
                  <td className="px-4 py-3 text-white">{a.full_name}</td>
                  <td className="px-4 py-3 text-zinc-300">
                    <a href={`mailto:${a.email}`} onClick={(e) => e.stopPropagation()} className="hover:text-[#FE7200]">{a.email}</a>
                  </td>
                  <td className="px-4 py-3 text-zinc-300">{a.phone || "—"}</td>
                  <td className="px-4 py-3 text-zinc-300 whitespace-nowrap">{a.country_region || "—"}</td>
                  <td className="px-4 py-3 text-zinc-300">{a.investment_readiness || "—"}</td>
                  <td className="px-4 py-3 text-zinc-300">{a.machines_interested || "—"}</td>
                  <td className="px-4 py-3 text-zinc-300 whitespace-nowrap">{a.desired_start_date || "—"}</td>
                  <td className="px-4 py-3 text-right">
                    <span
                      data-testid={`view-business-app-${i}`}
                      className="inline-flex items-center gap-1 rounded-md border border-white/15 bg-white/5 px-2.5 py-1 text-xs font-semibold text-zinc-200 hover:bg-white/10"
                    >
                      <Eye className="h-3.5 w-3.5" /> View
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selected && (
        <BusinessApplicationDetail application={selected} onClose={() => setSelected(null)} />
      )}
    </div>
  );
}

function BusinessApplicationDetail({ application: a, onClose }) {
  const rows = [
    ["Submitted", new Date(a.created_at).toLocaleString()],
    ["Source", a.source],
    ["Full name", a.full_name],
    ["Email", a.email],
    ["Phone", a.phone],
    ["Current occupation", a.current_occupation],
    ["Country / Region", a.country_region],
    ["Preferred city / state", a.preferred_city_state],
    ["Investment readiness", a.investment_readiness],
    ["Vending experience", a.vending_experience],
    ["Machines interested in", a.machines_interested],
    ["Desired start date", a.desired_start_date],
    ["How they heard about us", a.how_heard],
  ];

  return (
    <div
      data-testid="business-application-detail-modal"
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center px-4 py-8"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="w-full max-w-2xl max-h-full overflow-y-auto rounded-2xl border border-white/10 bg-[#0f0f10]">
        <div className="flex items-center justify-between p-5 border-b border-white/10 sticky top-0 bg-[#0f0f10]">
          <div>
            <h3 className="font-display text-lg font-bold">{a.full_name}</h3>
            <div className="mt-0.5 text-xs text-zinc-400">Vending business application · {a.source}</div>
          </div>
          <button
            onClick={onClose}
            data-testid="close-business-application-detail"
            className="rounded-md border border-white/15 bg-white/5 p-1.5 text-zinc-400 hover:text-white hover:bg-white/10"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="p-5">
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
            {rows.map(([label, value]) => (
              <div key={label}>
                <dt className="text-[11px] font-bold uppercase tracking-wider text-zinc-500">{label}</dt>
                <dd className="mt-0.5 text-sm text-white break-words">
                  {value && String(value).trim() ? (
                    label === "Email" ? (
                      <a href={`mailto:${value}`} className="text-[#FE7200] hover:underline">{value}</a>
                    ) : label === "Phone" ? (
                      <a href={`tel:${value}`} className="text-[#FE7200] hover:underline">{value}</a>
                    ) : (
                      String(value)
                    )
                  ) : (
                    <span className="text-zinc-500">—</span>
                  )}
                </dd>
              </div>
            ))}
          </dl>

          <div className="mt-6 pt-5 border-t border-white/10">
            <div className="text-[11px] font-bold uppercase tracking-wider text-zinc-500">Questions / Notes</div>
            <div className="mt-1.5 whitespace-pre-wrap rounded-lg border border-white/10 bg-[#1A1A1A] p-3.5 text-sm text-zinc-200 min-h-[80px]">
              {a.questions && a.questions.trim() ? a.questions : <span className="text-zinc-500">No questions provided.</span>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

